import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_URL } from "@/lib/api";
import axios from "axios";
import { PawPrint, FileText, DownloadSimple, Warning, Heart } from "@phosphor-icons/react";

/**
 * Public, token-scoped owner portal. No auth required — the URL contains the token.
 */
export default function OwnerPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios
      .get(`${API_URL}/portal/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Portal link is invalid or expired."));
  }, [token]);

  function downloadPdf(cid) {
    const url = `${API_URL}/portal/${token}/consultations/${cid}/pdf`;
    window.open(url, "_blank");
  }

  if (err) {
    return (
      <div className="min-h-screen bg-pf-bg flex items-center justify-center p-6">
        <div className="pf-card p-8 max-w-md text-center">
          <div className="h-14 w-14 rounded-full bg-pf-danger-bg grid place-items-center mx-auto mb-3">
            <Warning size={28} className="text-pf-danger" />
          </div>
          <h1 className="text-xl font-display font-bold text-pf-text">Link expired</h1>
          <p className="text-sm text-pf-text-secondary mt-2">{err}</p>
          <p className="text-xs text-pf-text-secondary mt-4">Please contact your clinic for a new link.</p>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-pf-bg grid place-items-center text-pf-text-secondary text-sm">Loading portal…</div>;

  const { patient, clinic, consultations } = data;

  return (
    <div className="min-h-screen bg-pf-bg text-pf-text">
      <header className="bg-white border-b border-pf-border sticky top-0 z-40">
        <div className="mx-auto max-w-3xl px-5 py-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-pf-primary-light grid place-items-center overflow-hidden">
            {clinic.logo_base64 ? (
              <img src={clinic.logo_base64} alt={clinic.name} className="w-full h-full object-cover" />
            ) : (
              <PawPrint size={22} weight="fill" className="text-pf-primary" />
            )}
          </div>
          <div>
            <div className="font-display font-bold text-lg text-pf-text">{clinic.name || "Veterinary Clinic"}</div>
            {clinic.phone && <div className="text-xs text-pf-text-secondary">{clinic.phone} · {clinic.email}</div>}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-8">
        <div className="pf-card p-6 mb-5">
          <div className="pf-label">Patient</div>
          <div className="flex items-center gap-4 mt-2">
            <div className="h-16 w-16 rounded-xl bg-pf-primary-light grid place-items-center">
              <Heart size={32} weight="fill" className="text-pf-accent" />
            </div>
            <div>
              <h1 className="text-3xl font-display font-extrabold tracking-tight">{patient.name}</h1>
              <div className="text-sm text-pf-text-secondary">
                {patient.species ? (patient.species === "dog" ? "Câine" : patient.species === "cat" ? "Pisică" : patient.species) : "—"} · {patient.breed || "—"}
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-pf-text-secondary">
            Welcome to your pet&apos;s health portal. Below you&apos;ll find every consultation report available for download.
          </p>
        </div>

        <div className="pf-card overflow-hidden">
          <div className="px-5 pt-4 pb-3 pf-section-title">Consultation history</div>
          {consultations.length === 0 ? (
            <div className="p-6 text-sm text-pf-text-secondary text-center">No consultations yet.</div>
          ) : (
            <ul className="divide-y divide-pf-border">
              {consultations.map((c) => (
                <li key={c.id} className="px-5 py-4 flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="h-9 w-9 rounded-full bg-pf-primary-light grid place-items-center shrink-0">
                      <FileText size={16} className="text-pf-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-pf-text">
                        {new Date(c.created_at).toLocaleDateString(undefined, {
                          year: "numeric", month: "long", day: "numeric",
                        })}
                      </div>
                      <div className="text-xs text-pf-text-secondary mt-0.5">
                        {(c.visit_reasons || []).join(", ") || "—"}
                      </div>
                      {c.diagnosis && (
                        <div className="text-xs text-pf-text-secondary mt-1 truncate">Dx: {c.diagnosis}</div>
                      )}
                    </div>
                  </div>
                  <button onClick={() => downloadPdf(c.id)} className="pf-btn-primary shrink-0">
                    <DownloadSimple size={14} /> PDF
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="text-center text-xs text-pf-text-secondary mt-8">
          Secured link · Powered by Pawfile
        </div>
      </main>
    </div>
  );
}
